/**
 * Tool Calling - Orchestrator for MCP tools in the Moviefone chat.
 *
 * This module handles:
 * 1. Tool definitions (JSON schema for the LLM)
 * 2. Tool call detection from LLM response text
 * 3. Tool execution
 * 4. Conversation management (formatting tool results as messages)
 */

import { ToolCall } from 'types/moviefone';
import {
  AssistantTool,
  ChatCompletionMessageToolCallChunk,
} from 'gen/stream-api/requests/types.gen';
import {
  getVolForecast,
  getVolForecastFull,
  getVrpTermStructure,
  getVixContext,
  getRegimeStatus,
  getImpliedEarningsMove,
  getSgoiPositioning,
  getScenarioPnl,
  getSurfaceEdgeRecommendation,
  getStrategyRecommendation,
  getAvailableStrategies,
  buildSpotgammaLink,
} from 'util/mcpTools';
import { $MoviefoneChatBody as MoviefoneChatBody } from 'gen/stream-api/requests/schemas.gen';
import { MoviefoneAnalysis } from 'gen/stream-api/requests/types.gen';

// ---------------------------------------------------------------------------
// Tool Definitions (JSON schema for the LLM)
// ---------------------------------------------------------------------------

// the text displayed at the end of a tool definition's description that indicates
// that the tool call is not to be fed back into the LLM for further processing
export const CLIENT_ONLY_POST_AMBLE = 'Display on the client side only.';

// This is just a convenience to avoid having to run the server or release to the
// server. These definitions should be moved to moviefone.rs when they're solidified.
export const CLIENT_DEFINED_TOOL_DEFINITIONS = [
  // TODO(ross) 2026.08.19 - this is a fake tool for testing
  // please keep as example
  // {
  //   name: 'show_three_trades',
  //   description: `Display a 3d surface of tickers options prices.  ${CLIENT_ONLY_POST_AMBLE}`,
  //   parameters: {
  //     type: 'object',
  //     properties: {
  //       ticker: { type: 'string', description: 'Ticker symbol' },
  //     },
  //     required: ['ticker'],
  //   },
  // },
];

const SERVER_DEFINED_TOOL_DEFINITIONS = (
  JSON.parse(
    MoviefoneChatBody.properties.client_side_tools.example,
  ) as AssistantTool[]
).map((assistantTool) => assistantTool.function);

export const TOOL_DEFINITIONS = [
  ...CLIENT_DEFINED_TOOL_DEFINITIONS,
  ...SERVER_DEFINED_TOOL_DEFINITIONS,
];

// ---------------------------------------------------------------------------
// Streaming Tool Call Accumulator
// ---------------------------------------------------------------------------

/**
 * Accumulates tool call arguments from streaming chunks.
 * Each chunk has delta.tool_calls[].index, .id, .type, .function.name, .function.arguments
 * Arguments arrive incrementally and must be concatenated per index.
 */
export class StreamingToolCallAccumulator {
  private calls: Map<number, { id: string; name: string; arguments: string }> =
    new Map();

  /**
   * Feed a chunk's tool_calls array into the accumulator.
   */
  addChunk(toolCalls: ChatCompletionMessageToolCallChunk[]): void {
    for (const tc of toolCalls) {
      const idx = tc.index ?? 0;
      const existing = this.calls.get(idx);
      if (existing != null) {
        // Accumulate arguments
        if (tc.function?.arguments != null) {
          existing.arguments += tc.function.arguments;
        }
      } else {
        this.calls.set(idx, {
          id: tc.id ?? `tool_call_${idx}`,
          name: tc.function?.name ?? '',
          arguments: tc.function?.arguments ?? '',
        });
      }
    }
  }

  /**
   * Get all accumulated tool calls as ToolCall objects.
   */
  getToolCalls(): ToolCall[] {
    return Array.from(this.calls.values()).map((tc) => ({
      id: tc.id,
      name: tc.name,
      args: (() => {
        try {
          return JSON.parse(tc.arguments) as Record<string, unknown>;
        } catch {
          console.warn(
            '[MCP Tool] Failed to parse tool call arguments:',
            tc.arguments,
          );
          return {};
        }
      })(),
    }));
  }

  /**
   * Check if any tool calls have been accumulated.
   */
  hasToolCalls(): boolean {
    return this.calls.size > 0;
  }

  /**
   * Reset the accumulator.
   */
  reset(): void {
    this.calls.clear();
  }
}

// ---------------------------------------------------------------------------
// Tool Call Detection (for regex-based parsing)
// ---------------------------------------------------------------------------

/**
 * Detect tool calls from LLM response text.
 *
 * The LLM outputs tool calls using this format:
 *   {"tool": "get_vol_forecast", "params": {"ticker": "SPY"}}
 *
 * @deprecated Use StreamingToolCallAccumulator for streaming responses
 */
export function detectToolCalls(responseText: string): ToolCall[] {
  const toolCallRegex = /<tool_call>(.*?)<\/tool_call>/gs;
  const matches = [...responseText.matchAll(toolCallRegex)];

  return matches
    .map((match, i) => {
      try {
        const parsed = JSON.parse(match[1]) as Record<string, unknown>;
        return {
          id: `tool_call_${i}`,
          name: (parsed.tool ?? parsed.name ?? '') as string,
          args: (parsed.params ?? parsed.parameters ?? {}) as Record<
            string,
            unknown
          >,
        };
      } catch {
        console.warn('[MCP Tool] Failed to parse tool call:', match[1]);
        return null;
      }
    })
    .filter((tc): tc is ToolCall => tc !== null);
}

// ---------------------------------------------------------------------------
// Tool Execution
// ---------------------------------------------------------------------------

/**
 * Execute a tool and return the result.
 */
export async function executeTool(
  toolName: string,
  params: Record<string, unknown>,
  data: MoviefoneAnalysis,
): Promise<object | null> {
  console.log(`[MCP Tool] Executing: ${toolName}`, params);

  let result: object | null = null;
  switch (toolName) {
    case 'get_vol_forecast':
      result = getVolForecast(data);
      break;
    case 'get_vol_forecast_full':
      result = getVolForecastFull(data);
      break;
    case 'get_vrp_term_structure':
      result = getVrpTermStructure(data);
      break;
    case 'get_vix_context':
      result = await getVixContext();
      break;
    case 'get_regime_status':
      result = getRegimeStatus(data);
      break;
    case 'get_implied_earnings_move':
      result = getImpliedEarningsMove(data);
      break;
    case 'get_sgoi_positioning':
      result = getSgoiPositioning(data);
      break;
    case 'get_scenario_pnl':
      result = getScenarioPnl(
        data,
        params as Parameters<typeof getScenarioPnl>[1],
      );
      break;
    case 'get_surface_edge_recommendation':
      result = getSurfaceEdgeRecommendation(
        data,
        params as Parameters<typeof getSurfaceEdgeRecommendation>[1],
      );
      break;
    case 'get_strategy_recommendation':
      result = getStrategyRecommendation(data);
      break;
    case 'get_available_strategies':
      result = getAvailableStrategies(data);
      break;
    case 'build_spotgamma_link': {
      const { symbol, legs } = params as {
        symbol: string;
        legs: Parameters<typeof buildSpotgammaLink>[0]['legs'];
      };
      result = { url: buildSpotgammaLink({ symbol, legs }) };
      break;
    }
    case 'run_analysis':
      // Something of a no-op. ticker symbol auto-intercepted at higher level
      // and `run_analysis` called there.

      result = { ui_action: 'show_three_trades' };
      break;
    case 'patch_trade_criteria':
      // Ignored - trade criteria come from /v1/moviefone/extract-structure-request.
      result = null;
      break;
    default:
      result = { error: `Unknown tool: ${toolName}` };
  }

  console.log(`[MCP Tool] Result:`, result);
  return result;
}

// ---------------------------------------------------------------------------
// Conversation Helpers
// ---------------------------------------------------------------------------

/**
 * Format a tool call and its result as a ChatMessage with role 'tool'.
 */
export function formatToolResultMessage(
  toolCall: ToolCall,
): import('types/moviefone').ChatMessage {
  return {
    role: 'tool',
    content: JSON.stringify(toolCall.result),
    toolName: toolCall.name,
    tool_call_id: toolCall.id,
  };
}

/**
 * Format a tool call as an assistant message that shows the LLM's request.
 */
export function formatToolCallAssistantMessage(
  toolCalls: ToolCall[],
): import('types/moviefone').ChatMessage {
  return {
    role: 'assistant',
    content: toolCalls
      .map(
        (tc) =>
          `<tool_call>${JSON.stringify({ tool: tc.name, params: tc.args })}</tool_call>`,
      )
      .join('\n'),
    toolCalls,
  };
}
